// components/PageWrapper.jsx
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {  FaGithub } from "react-icons/fa";
import { Link } from "react-router-dom";

const variants = {
  initial: { opacity: 0, y: -100, scale: 1.1 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.16, // etwas flüssiger
      duration: 0.55,
      ease: "easeInOut"
    }
  },
  exit: { opacity: 0, y: 100, scale: 1.05, transition: { duration: 0.4 } }
};

const childVariants = {
  initial: { opacity: 0, y: 20, filter: "blur(8px)" },
  animate: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      filter: { duration: 0.36 },
      y: { duration: 0.44 },
      opacity: { duration: 0.4 }
    }
  },
  exit: { opacity: 0, y: 100, filter: "blur(100px)", transition: { duration: 0.25 } }
};

export default function PageWrapper({
  children,
  className = "",
  style = {},
  animateChildren = true, // neuer prop
  disableMotion = false,
  layout = false, // nur bei Bedarf aktivieren!
  ...rest
}) {
  const [badgeOpen, setBadgeOpen] = useState(false);
  const badgeRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (!badgeOpen) return;
    const onKey = (e) => e.key === "Escape" && setBadgeOpen(false);
    const onClick = (e) => {
      if (!badgeRef.current) return;
      if (!badgeRef.current.contains(e.target)) setBadgeOpen(false);
    };
    const onScroll = () => setBadgeOpen(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("scroll", onScroll);
    };
  }, [badgeOpen]);

  const startAutoClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setBadgeOpen(false), 4000);
  };

  const stopAutoClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  useEffect(() => {
    if (!badgeOpen) return;
    startAutoClose();
    return () => stopAutoClose();
  }, [badgeOpen]);

  const wrappedChildren = Array.isArray(children)
    ? children.map((child, i) =>
        animateChildren ? (
          <motion.div key={child?.key ?? i} variants={childVariants}>
            {child}
          </motion.div>
        ) : (
          <>{child}</>
        )
      )
    : animateChildren ? (
        <motion.div variants={childVariants}>{children}</motion.div>
      ) : (
        <>{children}</>
      );

  return (
    <>
      {disableMotion ? (
        <div className={`relative ${className}`} style={style} {...rest}>
          {children}
        </div>
      ) : (
        <motion.div
          initial="initial"
          animate="animate"
          exit="exit"
          variants={variants}
          className={`relative ${className}`}
          style={style}
          {...(layout ? { layout } : {})}
          {...rest}
        >
          {wrappedChildren}
        </motion.div>
      )}

      {/* Global Corner Badge seite links zenstudio */}
      <div
        ref={badgeRef}
        className="fixed left-0 z-40 pointer-events-none"
        style={{ top: "calc(var(--navbar-height, 0px) - 38px)" }}
        onMouseEnter={stopAutoClose}
        onMouseLeave={startAutoClose}
        onFocus={stopAutoClose}
        onBlur={startAutoClose}
      >
       <div
  className={[
    "group origin-left transform will-change-transform pointer-events-none",
    "transition-transform duration-500",
    badgeOpen ? "translate-x-0" : "-translate-x-[87%]",
    // optional: wenn geschlossen, bei hover minimal „weiter raus“, wirkt wie Einladung ohne zu springen
    !badgeOpen ? "hover:translate-x-[-82%]" : ""
  ].join(" ")}
>
  <a
    href="https://github.com/THEORIGINALBITTER/zenpost-studio/releases/"
    target="_blank"
    rel="noopener noreferrer"
    className={[
      "inline-flex flex-row-reverse items-center gap-2",
      "mt-10 bg-[#AC8E66] rounded-r-lg",
      "text-[#151515] px-6 py-2",
      "text-[10px] uppercase tracking-[0.2em]",
      "font-mono font-semibold",
      "shadow-sm shadow-black/70",
      "border-r-0 border-[#151515]",
      "transition-all duration-500 ease-out",
      "group-hover:shadow-md",
      "group-hover:border-r-10",
      "focus-visible:outline-dotted",
      "focus-visible:ring-2 focus-visible:ring-[#b09062]/80",
      "pointer-events-auto",
    ].join(" ")}
    onClick={(e) => {
      if (!badgeOpen) {
        e.preventDefault();
        setBadgeOpen(true);
      }
    }}
  >
    <span
      className={[
        "text-sm leading-none text-[#151515]",
        "transition-transform duration-700 ease-out",
        "group-hover:translate-x-100"
      ].join(" ")}
    >
      <FaGithub />
    </span>

    <span
      className={[
        "transition-transform duration-700 ease-out",
       
      ].join(" ")}
    >
      ZenPost Studio Beta Release 1.8 auf
    </span>
  </a>

  {badgeOpen && (
    <div className="mt-2 w-[220px] bg-[#141414] text-[#EFEBDC] border border-[#AC8E66]/30 rounded-md overflow-hidden shadow-sm shadow-black/60 pointer-events-auto">
      <div className="px-3 pb-3 pt-2">
        <div className="text-[11px] leading-snug text-[#EFEBDC]/80 mb-2">
          ZenPost Studio Beta
          <br />
          <span className="text-[#AC8E66]">Jetzt ansehen</span>
        </div>

        <Link
          to="/zenpost-studio"
          className="block rounded focus-visible:outline-dotted focus-visible:ring-2 focus-visible:ring-[#b09062]/80"
          onClick={() => setBadgeOpen(false)}
          aria-label="Zur ZenEditor Landingpage"
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/zenpoststudio/ZenPost_02_editor.png`}
            alt="ZenPost Studio Preview"
            className="w-full h-25 rounded object-cover border border-[#AC8E66]/30"
            loading="lazy"
          />
        </Link>
      </div>
    </div>
  )}
</div>

      </div>
    </>
  );
}
